import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

export function KPICard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendType = "up", // 'up' | 'down' | 'neutral'
  color = "emerald", // 'emerald' | 'blue' | 'amber' | 'rose' | 'indigo' | 'purple'
  onClick,
  badgeText
}) {
  const colorMap = {
    emerald: {
      bg: "bg-emerald-500/10 dark:bg-emerald-500/20",
      text: "text-emerald-600 dark:text-emerald-400",
      border: "hover:border-emerald-500/50",
      glow: "group-hover:shadow-emerald-500/10"
    },
    blue: {
      bg: "bg-sky-500/10 dark:bg-sky-500/20",
      text: "text-sky-600 dark:text-sky-400",
      border: "hover:border-sky-500/50",
      glow: "group-hover:shadow-sky-500/10"
    },
    amber: {
      bg: "bg-amber-500/10 dark:bg-amber-500/20",
      text: "text-amber-600 dark:text-amber-400",
      border: "hover:border-amber-500/50",
      glow: "group-hover:shadow-amber-500/10"
    },
    rose: {
      bg: "bg-rose-500/10 dark:bg-rose-500/20",
      text: "text-rose-600 dark:text-rose-400",
      border: "hover:border-rose-500/50",
      glow: "group-hover:shadow-rose-500/10"
    },
    indigo: {
      bg: "bg-indigo-500/10 dark:bg-indigo-500/20",
      text: "text-indigo-600 dark:text-indigo-400",
      border: "hover:border-indigo-500/50",
      glow: "group-hover:shadow-indigo-500/10"
    },
    purple: {
      bg: "bg-purple-500/10 dark:bg-purple-500/20",
      text: "text-purple-600 dark:text-purple-400",
      border: "hover:border-purple-500/50",
      glow: "group-hover:shadow-purple-500/10"
    }
  };

  const scheme = colorMap[color] || colorMap.emerald;

  return (
    <div
      onClick={onClick}
      className={`group relative overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 p-5 shadow-sm hover:shadow-xl transition-all duration-300 ${
        scheme.glow
      } ${scheme.border} ${onClick ? "cursor-pointer transform hover:-translate-y-0.5" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {title}
          </p>
          <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white font-display">
            {value}
          </h3>
        </div>
        {Icon && (
          <div className={`rounded-xl p-3 ${scheme.bg} ${scheme.text} transition-transform duration-300 group-hover:scale-110`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between text-xs">
        {subtitle && (
          <span className="text-slate-500 dark:text-slate-400 truncate max-w-[180px]">
            {subtitle}
          </span>
        )}
        {trend && (
          <span
            className={`inline-flex items-center font-medium gap-1 px-2 py-0.5 rounded-full ${
              trendType === "up"
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400"
                : trendType === "down"
                ? "bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400"
                : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            }`}
          >
            {trendType === "up" && <TrendingUp className="w-3 h-3" />}
            {trendType === "down" && <TrendingDown className="w-3 h-3" />}
            {trendType === "neutral" && <Minus className="w-3 h-3" />}
            {trend}
          </span>
        )}
        {badgeText && (
          <span className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 px-2 py-0.5 rounded-md font-mono text-[11px]">
            {badgeText}
          </span>
        )}
      </div>
    </div>
  );
}
